import { isEqual } from 'lodash';
import React from 'react';
import { Alert } from 'react-bootstrap';

import { HelpField } from '@spinnaker/core';

import type { IEcsCapacityProviderStrategyItem, IEcsServerGroupCommand } from '../../serverGroupConfiguration.service';

export interface IEcsCapacityProviderProps {
  command: IEcsServerGroupCommand;
  onFieldChange: (key: string, value: any) => void;
  configureCommand: (query: string) => Promise<void>;
}

interface IEcsCapacityProviderState {
  capacityProviderStrategy: IEcsCapacityProviderStrategyItem[];
  defaultCapacityProviderStrategy: IEcsCapacityProviderStrategyItem[];
  availableCapacityProviders: string[];
  ecsClusterName: string;
  useDefaultCapacityProviders: boolean;
  capacityProviderLoadedFlag: boolean;
  activeCapacityProviderIndex: number | null;
}

export class EcsCapacityProvider extends React.Component<IEcsCapacityProviderProps, IEcsCapacityProviderState> {
  constructor(props: IEcsCapacityProviderProps) {
    super(props);
    const cmd = this.props.command;

    this.state = {
      availableCapacityProviders: this.getAvailableCapacityProviders(cmd),
      defaultCapacityProviderStrategy: this.getDefaultCapacityProviderStrategy(cmd),
      ecsClusterName: cmd.ecsClusterName,
      useDefaultCapacityProviders: cmd.useDefaultCapacityProviders !== false,
      capacityProviderStrategy:
        cmd.capacityProviderStrategy && cmd.capacityProviderStrategy.length > 0 ? cmd.capacityProviderStrategy : [],
      capacityProviderLoadedFlag: false,
      activeCapacityProviderIndex: null,
    };
  }

  public componentDidMount() {
    this.props.configureCommand('1').then(() => {
      const cmd = this.props.command;
      const useDefaultCapacityProviders = this.state.useDefaultCapacityProviders;
      const capacityProviderStrategy = this.getCapacityProviderStrategy(useDefaultCapacityProviders, cmd);
      this.setState(
        {
          availableCapacityProviders: this.getAvailableCapacityProviders(cmd),
          defaultCapacityProviderStrategy: this.getDefaultCapacityProviderStrategy(cmd),
          capacityProviderStrategy,
          capacityProviderLoadedFlag: true,
        },
        () => {
          this.props.onFieldChange('useDefaultCapacityProviders', useDefaultCapacityProviders);
          this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
        },
      );
    });
  }

  public componentDidUpdate() {
    const cmd = this.props.command;
    const useDefaultCapacityProviders = cmd.useDefaultCapacityProviders !== false;
    const commandState = {
      availableCapacityProviders: this.getAvailableCapacityProviders(cmd),
      defaultCapacityProviderStrategy: this.getDefaultCapacityProviderStrategy(cmd),
      ecsClusterName: cmd.ecsClusterName,
      useDefaultCapacityProviders,
      capacityProviderStrategy: useDefaultCapacityProviders
        ? this.getDefaultCapacityProviderStrategy(cmd)
        : cmd.capacityProviderStrategy || [],
    };
    const currentState = {
      availableCapacityProviders: this.state.availableCapacityProviders,
      defaultCapacityProviderStrategy: this.state.defaultCapacityProviderStrategy,
      ecsClusterName: this.state.ecsClusterName,
      useDefaultCapacityProviders: this.state.useDefaultCapacityProviders,
      capacityProviderStrategy: this.state.capacityProviderStrategy,
    };
    if (!isEqual(currentState, commandState)) {
      const strategyChanged = !isEqual(cmd.capacityProviderStrategy || [], commandState.capacityProviderStrategy);
      this.setState(commandState, () => {
        if (strategyChanged) {
          this.props.onFieldChange('capacityProviderStrategy', commandState.capacityProviderStrategy);
        }
      });
    }
  }

  private getAvailableCapacityProviders = (cmd: IEcsServerGroupCommand) => {
    const available = cmd.backingData?.filtered?.availableCapacityProviders || [];
    const persisted = (cmd.capacityProviderStrategy || []).map((strategy) => strategy.capacityProvider);
    return Array.from(new Set([...available, ...persisted])).filter(Boolean);
  };

  private getDefaultCapacityProviderStrategy = (cmd: IEcsServerGroupCommand) => {
    return cmd.backingData && cmd.backingData.filtered && cmd.backingData.filtered.defaultCapacityProviderStrategy
      ? cmd.backingData.filtered.defaultCapacityProviderStrategy
      : [];
  };

  private getCapacityProviderStrategy = (useDefaultCapacityProviders: boolean, cmd: IEcsServerGroupCommand) => {
    return useDefaultCapacityProviders &&
      cmd.backingData &&
      cmd.backingData.filtered &&
      cmd.backingData.filtered.defaultCapacityProviderStrategy
      ? cmd.backingData.filtered.defaultCapacityProviderStrategy
      : this.state.capacityProviderStrategy;
  };

  private addCapacityProviderStrategy = () => {
    const capacityProviderStrategy = this.state.capacityProviderStrategy;
    capacityProviderStrategy.push({ capacityProvider: '', base: null, weight: null });
    this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
    this.setState({ capacityProviderStrategy: capacityProviderStrategy });
  };

  private removeCapacityProviderStrategy = (index: number) => {
    const capacityProviderStrategy = this.state.capacityProviderStrategy;
    capacityProviderStrategy.splice(index, 1);
    this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
    this.setState({ capacityProviderStrategy: capacityProviderStrategy });
  };

  private updateCapacityProviderName = (index: number, targetCapacityProviderName: string) => {
    const capacityProviderStrategy = this.state.capacityProviderStrategy;
    const targetCapacityProviderStrategy = capacityProviderStrategy[index];
    targetCapacityProviderStrategy.capacityProvider = targetCapacityProviderName;
    this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
    this.setState({ capacityProviderStrategy: capacityProviderStrategy });
    this.props.command.viewState.dirty.customCapacityProviders = [];
  };

  private updateCapacityProviderBase = (index: number, targetCapacityProviderBase: number) => {
    const capacityProviderStrategy = this.state.capacityProviderStrategy;
    const targetCapacityProviderStrategy = capacityProviderStrategy[index];
    targetCapacityProviderStrategy.base = targetCapacityProviderBase;
    this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
    this.setState({ capacityProviderStrategy: capacityProviderStrategy });
  };

  private updateCapacityProviderWeight = (index: number, targetCapacityProviderWeight: number) => {
    const capacityProviderStrategy = this.state.capacityProviderStrategy;
    const targetCapacityProviderStrategy = capacityProviderStrategy[index];
    targetCapacityProviderStrategy.weight = targetCapacityProviderWeight;
    this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
    this.setState({ capacityProviderStrategy: capacityProviderStrategy });
  };

  private updateCapacityProviderType = (targetCapacityProviderType: boolean) => {
    this.setState({ useDefaultCapacityProviders: targetCapacityProviderType });
    this.props.onFieldChange('useDefaultCapacityProviders', targetCapacityProviderType);
    this.props.command.viewState.dirty.customCapacityProviders = [];

    const capacityProviderStrategy =
      targetCapacityProviderType && this.state.defaultCapacityProviderStrategy.length > 0
        ? this.state.defaultCapacityProviderStrategy
        : [];
    this.setState({ capacityProviderStrategy: capacityProviderStrategy });
    this.props.onFieldChange('capacityProviderStrategy', capacityProviderStrategy);
  };

  render(): React.ReactElement<EcsCapacityProvider> {
    const updateCapacityProviderName = this.updateCapacityProviderName;
    const updateCapacityProviderBase = this.updateCapacityProviderBase;
    const updateCapacityProviderWeight = this.updateCapacityProviderWeight;
    const addCapacityProviderStrategy = this.addCapacityProviderStrategy;
    const removeCapacityProviderStrategy = this.removeCapacityProviderStrategy;
    const updateCapacityProviderType = this.updateCapacityProviderType;
    const capacityProviderStrategy = this.state.capacityProviderStrategy;
    const useDefaultCapacityProviders = this.state.useDefaultCapacityProviders;
    const capacityProviderLoadedFlag = this.state.capacityProviderLoadedFlag;
    const customDirtyCapacityProviders =
      this.props.command.viewState.dirty && this.props.command.viewState.dirty.customCapacityProviders
        ? this.props.command.viewState.dirty.customCapacityProviders
        : [];
    const defaultDirtyCapacityProviders =
      this.props.command.viewState.dirty && this.props.command.viewState.dirty.defaulCapacityProviders
        ? this.props.command.viewState.dirty.defaulCapacityProviders
        : [];

    const capacityProviderNames =
      this.state.availableCapacityProviders && this.state.availableCapacityProviders.length > 0
        ? this.state.availableCapacityProviders.map((capacityProviderNames) => {
            return { label: `${capacityProviderNames}`, value: capacityProviderNames };
          })
        : [];

    const defaultCPError =
      useDefaultCapacityProviders && defaultDirtyCapacityProviders ? (
        <div className="alert alert-warning">
          <p className="text-left">
            <i className="fa fa-exclamation-triangle"></i>
            Invalid capacity providers are a part of default capacity provider strategy.
            <br />
            Please click 'Done' to use current default capacity provider strategy (shown below) or switch to using a
            custom strategy.
          </p>
        </div>
      ) : (
        ''
      );

    const dirtyCapacityProviderList = customDirtyCapacityProviders
      ? customDirtyCapacityProviders.map(function (capacityProvider: string, index: number) {
          return <li key={index}>{capacityProvider}</li>;
        })
      : '';

    const dirtyCapacityProviderSection =
      useDefaultCapacityProviders && defaultDirtyCapacityProviders.length > 0 ? (
        defaultCPError
      ) : customDirtyCapacityProviders.length > 0 && !useDefaultCapacityProviders ? (
        <div className="alert alert-warning">
          <p>
            <i className="fa fa-exclamation-triangle"></i>
            The following capacity providers could not be found in the selected account/region/cluster and were removed:
          </p>
          <ul>{dirtyCapacityProviderList}</ul>
          <br />
          <p className="text-left">Please select the capacity provider(s) from the dropdown to resolve this error.</p>
        </div>
      ) : (
        ''
      );

    const capacityProviderInputs =
      capacityProviderStrategy.length > 0 ? (
        capacityProviderStrategy.map((mapping, index) => {
          return (
            <tr key={index}>
              {useDefaultCapacityProviders ? (
                <td>
                  <input
                    aria-label={`Capacity provider name ${index + 1}`}
                    data-test-id={'ServerGroup.defaultCapacityProvider.name.' + index}
                    type="string"
                    className="form-control input-sm no-spel"
                    required={true}
                    value={mapping.capacityProvider}
                    disabled={true}
                  />
                </td>
              ) : (
                <td>
                  <input
                    aria-label={`Capacity provider name ${index + 1}`}
                    className="form-control input-sm"
                    data-test-id={'ServerGroup.customCapacityProvider.name.' + index}
                    onChange={(event) => {
                      this.setState({ activeCapacityProviderIndex: index });
                      updateCapacityProviderName(index, event.target.value);
                    }}
                    onFocus={() => this.setState({ activeCapacityProviderIndex: index })}
                    type="text"
                    value={mapping.capacityProvider || ''}
                  />
                  {this.state.activeCapacityProviderIndex === index &&
                    capacityProviderNames.map((capacityProvider) => (
                      <button
                        className="Select-option"
                        key={capacityProvider.value}
                        onClick={() => {
                          updateCapacityProviderName(index, capacityProvider.value);
                          this.setState({ activeCapacityProviderIndex: null });
                        }}
                        type="button"
                      >
                        {capacityProvider.label}
                      </button>
                    ))}
                </td>
              )}
              <td>
                <input
                  aria-label={`Capacity provider base ${index + 1}`}
                  data-test-id={'ServerGroup.capacityProvider.base.' + index}
                  disabled={useDefaultCapacityProviders}
                  type="number"
                  className="form-control input-sm no-spel"
                  value={mapping.base}
                  onChange={(e) => updateCapacityProviderBase(index, e.target.valueAsNumber)}
                />
              </td>
              <td>
                <input
                  aria-label={`Capacity provider weight ${index + 1}`}
                  data-test-id={'ServerGroup.capacityProvider.weight.' + index}
                  disabled={useDefaultCapacityProviders}
                  type="number"
                  className="form-control input-sm no-spel"
                  required={true}
                  value={mapping.weight}
                  onChange={(e) => updateCapacityProviderWeight(index, e.target.valueAsNumber)}
                />
              </td>
              {!useDefaultCapacityProviders ? (
                <td>
                  <div className="form-control-static">
                    <button
                      aria-label={`Remove capacity provider ${index + 1}`}
                      className="btn-link sm-label"
                      onClick={() => removeCapacityProviderStrategy(index)}
                      type="button"
                    >
                      <span className="glyphicon glyphicon-trash" />
                      <span className="sr-only">Remove</span>
                    </button>
                  </div>
                </td>
              ) : (
                ''
              )}
            </tr>
          );
        })
      ) : useDefaultCapacityProviders && this.state.capacityProviderStrategy.length == 0 ? (
        <tr>
          <div className="sm-label-left" style={{ width: '200%' }}>
            <Alert color="warning">
              {' '}
              The cluster does not have a default capacity provider strategy defined. Set a default capacity provider
              strategy or use a custom strategy.
            </Alert>
          </div>
        </tr>
      ) : (
        ''
      );

    const newCapacityProviderStrategy =
      this.state.ecsClusterName &&
      this.props.command.credentials &&
      this.props.command.region &&
      !useDefaultCapacityProviders &&
      capacityProviderNames.length > 0 ? (
        <button
          data-test-id="ServerGroup.addCapacityProvider"
          className="btn btn-block btn-sm add-new"
          onClick={addCapacityProviderStrategy}
          type="button"
        >
          <span className="glyphicon glyphicon-plus-sign" />
          Add New Capacity Provider
        </button>
      ) : !useDefaultCapacityProviders && capacityProviderLoadedFlag && capacityProviderNames.length == 0 ? (
        <div className="sm-label-left" style={{ width: '200%' }}>
          <Alert color="warning"> The cluster does not have capacity providers defined. </Alert>
        </div>
      ) : (
        ''
      );

    return (
      <div>
        {(customDirtyCapacityProviders.length > 0 || defaultDirtyCapacityProviders.length > 0) &&
        capacityProviderLoadedFlag ? (
          <div>{dirtyCapacityProviderSection}</div>
        ) : (
          ''
        )}
        <div className="sm-label-left">
          <b>Capacity Provider Strategy</b>
          <HelpField id="ecs.capacityProviderStrategy" /> <br />
          <span>({this.state.ecsClusterName})</span>
        </div>
        <div className="radio">
          <label>
            <input
              data-test-id="ServerGroup.capacityProviders.default"
              type="radio"
              checked={useDefaultCapacityProviders}
              onClick={() => updateCapacityProviderType(true)}
              id="computeOptionsLaunchType1"
            />
            Use cluster default
          </label>
        </div>
        <div className="radio">
          <label>
            <input
              data-test-id="ServerGroup.capacityProviders.custom"
              type="radio"
              checked={!useDefaultCapacityProviders}
              onClick={() => updateCapacityProviderType(false)}
              id="computeOptionsCapacityProviders2"
            />
            Use custom (Advanced)
          </label>
        </div>
        {capacityProviderLoadedFlag ? (
          <table className="table table-condensed packed tags">
            <thead>
              <tr>
                <th style={{ width: '50%' }}>
                  Provider name
                  <HelpField id="ecs.capacityProviderName" />
                </th>
                <th style={{ width: '25%' }}>
                  Base
                  <HelpField id="ecs.capacityProviderBase" />
                </th>
                <th style={{ width: '25%' }}>
                  Weight
                  <HelpField id="ecs.capacityProviderWeight" />
                </th>
              </tr>
            </thead>
            <tbody>{capacityProviderInputs}</tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>{newCapacityProviderStrategy}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="load medium">
            <div className="message">Loading capacity providers...</div>
            <div className="bars">
              <div className="bar"></div>
              <div className="bar"></div>
              <div className="bar"></div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
